import { useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { FileDown, Upload, X, CheckCircle2, Clock, FileText } from 'lucide-react'
import { api } from '../lib/api'
import { Button } from '@/components/ui/button'
import { extractErr } from '@/components/ui/inline-edit'
import { blobErr } from '../lib/blobErr'

type GeneratedDocument = {
  id: number
  template: number
  template_category: string
  template_name: string | null
  template_version: number
  /** Inherited from the template — drives per-row signing UI. */
  template_signable: boolean
  source_type: string
  source_id: number
  language_code: string
  generated_file_id: string | null
  signed_file_id: string | null
  status: 'DRAFT' | 'AWAITING_SIGNATURE' | 'SIGNED' | 'VOID'
  generated_at: string
  generated_by_username: string | null
  sent_for_signature_at: string | null
  signed_at: string | null
  voided_at: string | null
  voided_reason: string
  notes: string
}

interface Props {
  sourceType: string
  sourceId: number
}

const STATUS_COLOR: Record<GeneratedDocument['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  AWAITING_SIGNATURE: 'bg-amber-100 text-amber-800',
  SIGNED: 'bg-emerald-100 text-emerald-800',
  VOID: 'bg-red-100 text-red-700',
}

const STATUS_LABEL: Record<GeneratedDocument['status'], string> = {
  DRAFT: 'Draft',
  AWAITING_SIGNATURE: 'Awaiting signature',
  SIGNED: 'Signed',
  VOID: 'Void',
}

export default function GeneratedDocumentsList({ sourceType, sourceId }: Props) {
  const qc = useQueryClient()

  const docsQ = useQuery({
    queryKey: ['generated-documents', sourceType, sourceId],
    queryFn: async () => {
      const r = await api.get('/generated-documents/', {
        params: { source_type: sourceType, source_id: sourceId },
      })
      const body = r.data
      if (Array.isArray(body)) return body as GeneratedDocument[]
      if (body && Array.isArray(body.results)) return body.results as GeneratedDocument[]
      return [] as GeneratedDocument[]
    },
  })

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['generated-documents', sourceType, sourceId] })

  const sendMut = useMutation({
    mutationFn: async (id: number) =>
      (await api.post(`/generated-documents/${id}/send-for-signature/`)).data,
    onSuccess: () => { invalidate(); toast.success('Marked awaiting signature') },
    onError: (e) => toast.error(extractErr(e)),
  })

  const voidMut = useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) =>
      (await api.post(`/generated-documents/${id}/void/`, { reason })).data,
    onSuccess: () => { invalidate(); toast.success('Document voided') },
    onError: (e) => toast.error(extractErr(e)),
  })

  const uploadMut = useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const fd = new FormData()
      fd.append('signed', file)
      return (await api.post(`/generated-documents/${id}/upload-signed/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })).data
    },
    onSuccess: () => { invalidate(); toast.success('Signed copy uploaded') },
    onError: (e) => toast.error(extractErr(e)),
  })

  const downloadDoc = (docId: string, suggestedName: string) => {
    api.get(`/documents/${docId}/download/`, { responseType: 'blob' })
      .then((res) => {
        const blob = new Blob([res.data])
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = suggestedName
        link.click()
        window.URL.revokeObjectURL(link.href)
      })
      .catch(async (e) => toast.error(await blobErr(e)))
  }

  const docs = docsQ.data || []
  const anySignable = docs.some((d) => d.template_signable)
  const awaiting = docs.find((d) => d.template_signable && d.status === 'AWAITING_SIGNATURE')
  const latestSigned = docs.find((d) => d.template_signable && d.status === 'SIGNED')

  return (
    <div className="space-y-3">
      {/* Status banners — only when signing workflow applies */}
      {awaiting && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 flex items-center gap-3 text-sm">
          <Clock className="h-4 w-4 text-amber-700 shrink-0" />
          <span className="text-amber-900">
            <span className="font-semibold">{awaiting.template_category}</span> sent for signature on{' '}
            {awaiting.sent_for_signature_at ? new Date(awaiting.sent_for_signature_at).toLocaleDateString() : '—'}.
          </span>
        </div>
      )}
      {!awaiting && latestSigned && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 flex items-center gap-3 text-sm">
          <CheckCircle2 className="h-4 w-4 text-emerald-700 shrink-0" />
          <span className="text-emerald-900">
            Latest <span className="font-semibold">{latestSigned.template_category}</span> signed on{' '}
            {latestSigned.signed_at ? new Date(latestSigned.signed_at).toLocaleDateString() : '—'}.
          </span>
        </div>
      )}

      {/* Document list */}
      {docsQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : docs.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-6 text-center text-sm text-muted-foreground">
          No generated documents yet. Use the "Generate document" button above.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground bg-muted/30 border-b">
              <tr>
                <th className="text-left px-4 py-2">Category</th>
                <th className="text-left px-4 py-2">Lang</th>
                <th className="text-left px-4 py-2">Generated</th>
                {anySignable && <th className="text-center px-4 py-2">Status</th>}
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {docs.map((d) => (
                <Row key={d.id} d={d} showStatusColumn={anySignable}
                     onSend={() => sendMut.mutate(d.id)}
                     onVoid={() => {
                       const r = window.prompt('Reason for voiding (optional)')
                       if (r === null) return
                       voidMut.mutate({ id: d.id, reason: r })
                     }}
                     onUpload={(file) => uploadMut.mutate({ id: d.id, file })}
                     onDownload={(which) => {
                       const id = which === 'signed' ? d.signed_file_id : d.generated_file_id
                       if (!id) return
                       const base = (d.template_name || d.template_category).replace(/[^\w]/g, '_')
                       const ext = which === 'signed' ? 'pdf' : 'pdf'
                       downloadDoc(id, `${base}-${d.source_id}-${which}.${ext}`)
                     }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Row({
  d, showStatusColumn, onSend, onVoid, onUpload, onDownload,
}: {
  d: GeneratedDocument
  /** Whether the table renders a Status column at all (driven by parent
   * across all rows; the per-row Status cell renders dash when this row's
   * template isn't signable). */
  showStatusColumn: boolean
  onSend: () => void
  onVoid: () => void
  onUpload: (file: File) => void
  onDownload: (which: 'generated' | 'signed') => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const signable = d.template_signable
  return (
    <tr className="hover:bg-muted/30">
      <td className="px-4 py-2">
        <div className="font-medium">{d.template_name || d.template_category}</div>
        <div className="text-xs text-muted-foreground font-mono">
          {d.template_category} · v{d.template_version}
        </div>
      </td>
      <td className="px-4 py-2 font-mono uppercase text-muted-foreground text-xs">
        {d.language_code || '—'}
      </td>
      <td className="px-4 py-2 text-muted-foreground text-xs">
        {new Date(d.generated_at).toLocaleDateString()}
        {d.generated_by_username && (
          <div className="text-muted-foreground/70">by {d.generated_by_username}</div>
        )}
      </td>
      {showStatusColumn && (
        <td className="px-4 py-2 text-center">
          {signable ? (
            <>
              <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_COLOR[d.status]}`}>
                {STATUS_LABEL[d.status]}
              </span>
              {d.status === 'VOID' && d.voided_reason && (
                <div className="text-[10px] italic text-muted-foreground mt-1 max-w-[140px] mx-auto truncate"
                     title={d.voided_reason}>
                  {d.voided_reason}
                </div>
              )}
            </>
          ) : (
            <span className="text-xs text-muted-foreground italic">—</span>
          )}
        </td>
      )}
      <td className="px-4 py-2 text-right space-x-2">
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.jpg,.jpeg,.png"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) onUpload(f)
            e.target.value = ''
          }}
        />
        {d.generated_file_id && (
          <button
            type="button"
            title="Download generated"
            onClick={() => onDownload('generated')}
            className="inline-flex items-center text-xs px-2 py-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            <FileDown className="h-3.5 w-3.5 mr-1" /> original
          </button>
        )}
        {d.signed_file_id && (
          <button
            type="button"
            title="Download signed copy"
            onClick={() => onDownload('signed')}
            className="inline-flex items-center text-xs px-2 py-1 rounded hover:bg-muted text-emerald-700"
          >
            <FileText className="h-3.5 w-3.5 mr-1" /> signed
          </button>
        )}
        {signable && d.status === 'DRAFT' && (
          <button
            type="button"
            onClick={onSend}
            className="text-xs px-2 py-1 rounded hover:bg-muted text-blue-600"
          >
            Send for signature
          </button>
        )}
        {signable && d.status === 'AWAITING_SIGNATURE' && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center text-xs px-2 py-1 rounded hover:bg-muted text-emerald-700"
          >
            <Upload className="h-3.5 w-3.5 mr-1" /> Upload signed
          </button>
        )}
        {signable && d.status !== 'VOID' && (
          <button
            type="button"
            onClick={onVoid}
            title="Void"
            className="inline-flex items-center text-xs px-2 py-1 rounded hover:bg-muted text-red-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </td>
    </tr>
  )
}
